<template>
  <SfList no-margin data-testid="assistant-checks-left-sidepane">
    <slot name="before"></slot>
    <SfListItem
      item-id="checks-unread"
      background="transparent"
      font-weight="medium"
      cornered
      :selected="selectedCategory === UNREAD_CHECKS"
      @item-click="setSelected(UNREAD_CHECKS, '')"
    >
      <template #icon>
        <i class="icon icon-envelope-open" />
      </template>

      {{ I18n.t('assistant_check.page.sections.unread') }}

      <template #right-content>
        <span
          v-if="totalUnreadChecks > 0"
          class="
            tw-flex
            tw-items-center
            tw-justify-center
            tw-h-4
            tw-min-w-[16px]
            tw-rounded-full
            tw-px-1
            tw-leading-3
            tw-bg-sf-danger
            tw-text-sf-snow-lighter
            tw-text-xs
          "
          data-testid="assistant-checks-unread-count"
        >
          {{ totalUnreadChecks }}
        </span>
      </template>
    </SfListItem>
    <SfListItem
      v-if="Object.keys(checksById).length"
      item-id="checks-all"
      background="transparent"
      font-weight="medium"
      cornered
      :selected="selectedCategory === ALL_CHECKS"
      @item-click="setSelected(ALL_CHECKS, '')"
    >
      {{ I18n.t('assistant_check.page.sections.all') }}

      <template #right-content>
        <UnreadIndicator v-if="totalUnreadChecks > 0" />
      </template>
    </SfListItem>
    <SfListItem
      v-if="generalCheckIds.length"
      item-id="checks-general"
      background="transparent"
      font-weight="medium"
      cornered
      :selected="selectedCategory === GENERAL_CHECKS"
      @item-click="setSelected(GENERAL_CHECKS, '')"
    >
      {{ I18n.t('assistant_check.page.sections.general') }}

      <template #right-content>
        <UnreadIndicator v-if="generalUnreadCheckIds.length > 0" />
      </template>
    </SfListItem>
    <SfListItemGroup
      v-for="workflowId in workflowIds"
      :key="workflowId"
      :initially-expanded="workflowById[workflowId].expanded"
      cornered
      :item-id="`checks-${workflowId}`"
      background="transparent"
      :header="workflowById[workflowId].name"
    >
      <SfListItem
        v-for="contextId in groupedContextIds[workflowId]"
        :key="`${workflowId}-${contextId}`"
        background="transparent"
        cornered
        :selected="
          selectedCategory === `${workflowId}` &&
          selectedItem === `${contextId}`
        "
        :item-id="`checks-${workflowId}-${contextId}`"
        @item-click="setSelected(`${workflowId}`, `${contextId}`)"
      >
        {{ contextById[contextId].name }}

        <template #right-content>
          <UnreadIndicator
            v-if="groupedUnreadCheckIds[workflowId][contextId].length > 0"
          />
        </template>
      </SfListItem>

      <template #right-content>
        <UnreadIndicator
          v-if="groupedUnreadCheckIdsByWorkflow[workflowId].length > 0"
        />
      </template>
    </SfListItemGroup>
  </SfList>
</template>

<script setup lang="ts">
import useI18n from '@app/shared/vue/composables/use-I18n'
import { storeToRefs } from 'pinia'

import SfListItemGroup from '@app/shared/vue/components/sf-list/sf-list-item-group.vue'
import SfListItem from '@app/shared/vue/components/sf-list/sf-list-item.vue'
import SfList from '@app/shared/vue/components/sf-list/sf-list.vue'
import UnreadIndicator from '@app/shared/vue/components/unread-indicator.vue'
import { useSfAssistantChecksPage } from '../stores/page'

import {
  UNREAD_CHECKS,
  ALL_CHECKS,
  GENERAL_CHECKS,
} from '@app/application/sf-assistant-checks/utils'

interface Props {
  selectedCategory: string
  selectedItem: string
}

defineProps<Props>()

const I18n = useI18n()

const sfAssistantChecksPageStore = useSfAssistantChecksPage()
const {
  groupedUnreadCheckIdsByWorkflow,
  groupedUnreadCheckIds,
  generalUnreadCheckIds,
  totalUnreadChecks,
  groupedContextIds,
  contextById,
  workflowIds,
  workflowById,
  checksById,
  generalCheckIds,
} = storeToRefs(sfAssistantChecksPageStore)

const emit =
  defineEmits<{
    select: [string, string]
  }>()

function setSelected(category: string, item: string): void {
  emit('select', category, item)
}
</script>
